import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import content from '../content/site-content.ko.json';
import PageHeader from './PageHeader';
import './Product.css';

// 텍스트를 마침표 기준으로 줄바꿈하는 함수
const formatText = (text) => {
  return text.split('. ').map((sentence, index, array) => (
    <React.Fragment key={index}>
      {sentence}{index < array.length - 1 ? '.' : ''}
      {index < array.length - 1 && <br />}
    </React.Fragment>
  ));
};

function Product({ legacy = false }) {
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationProduct, setReservationProduct] = useState('');
  const [reservationForm, setReservationForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: ''
  });
  const [reservationStatus, setReservationStatus] = useState({ type: '', message: '' });
  const [reservationLoading, setReservationLoading] = useState(false);

  const openReservation = (productName) => {
    setReservationProduct(productName);
    setReservationStatus({ type: '', message: '' });
    setShowReservationModal(true);
  };

  const closeReservation = useCallback(() => {
    setShowReservationModal(false);
    setReservationForm({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
    setReservationStatus({ type: '', message: '' });
    setReservationProduct('');
  }, []);

  // ESC 키로 모달 닫기 및 포커스 관리
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showReservationModal) {
        closeReservation();
      }
    };

    if (showReservationModal) {
      document.addEventListener('keydown', handleEscape);
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
      
      // 모달이 열릴 때 첫 번째 입력 필드에 포커스
      setTimeout(() => {
        const firstInput = document.querySelector('.reservation-form input[name="name"]');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showReservationModal, closeReservation]);

  // 성공 시 자동으로 모달 닫기
  useEffect(() => {
    if (reservationStatus.type === 'success') {
      const timer = setTimeout(() => {
        closeReservation();
      }, 3000); // 3초 후 자동 닫기

      return () => clearTimeout(timer);
    }
  }, [reservationStatus.type, closeReservation]);

  const handleReservationChange = (e) => {
    const { name, value } = e.target;
    setReservationForm((prev) => ({
      ...prev,
      [name]: value
    }));
    setReservationStatus({ type: '', message: '' });
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();

    if (!reservationForm.name || !reservationForm.email) {
      setReservationStatus({ type: 'error', message: '이름과 이메일을 입력해주세요.' });
      return;
    }

    setReservationLoading(true);
    setReservationStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: reservationForm.name,
          email: reservationForm.email,
          phone: reservationForm.phone,
          website: reservationForm.website,
          subject: `[예약 문의] ${reservationProduct}`,
          message: `
제품: ${reservationProduct}
신청자: ${reservationForm.name}
이메일: ${reservationForm.email}
연락처: ${reservationForm.phone || '미입력'}

요청 사항:
${reservationForm.message || '미입력'}
          `.trim()
        })
      });

      if (response.ok) {
        setReservationStatus({ type: 'success', message: '예약 신청이 접수되었습니다. 담당자가 곧 연락드립니다.' });
        setReservationForm({
          name: '',
          email: '',
          phone: '',
          message: '',
          website: ''
        });
        // 성공 시 자동 닫기는 useEffect에서 처리
      } else {
        const data = await response.json().catch(() => ({}));
        setReservationStatus({ type: 'error', message: data.error || '예약 신청에 실패했습니다. 잠시 후 다시 시도해주세요.' });
      }
    } catch (error) {
      setReservationStatus({ type: 'error', message: '서버와 통신할 수 없습니다. 잠시 후 다시 시도해주세요.' });
    } finally {
      setReservationLoading(false);
    }
  };

  return (
    <div className="product-page">
      {legacy && (
        <div className="legacy-banner">
          <p>{content.legacy.banner}</p>
          <Link to="/">{content.legacy.backLink}</Link>
        </div>
      )}
      <PageHeader
        title="Our Products" 
        subtitle="모니터를 벗어난난 AI와 언어를 배우고 여행에선 동반자가 되는 세상" 
        backgroundImage={process.env.PUBLIC_URL + "/images/product-bg.jpg"}
      />
      <div className="product-container">
        <h2 className="product-intro">
          <span className="highlight">AI</span>와 <span className="highlight">3D 기술</span>로 혁신적인 언어 학습과 에이전트 경험을 제공합니다
        </h2>
        <div className="product-separator"></div>
        <p className="product-description">
          {formatText("Star612는 AI, AI생성 영상과 로봇 기술을 결합하여 혁신적인 언어 학습/통역 솔루션을 제공합니다. 우리의 제품은 학습자들에게 몰입감 있고 효과적인 언어 학습 경험을 선사합니다.")}
        </p>
        
        <div className="product-highlight-section">
          <div className="product-highlight-box">
            <img src={process.env.PUBLIC_URL + "/images/ai01.png"} alt="AI 대화모델" />
            <h3>AI 대화모델</h3>
          </div>
          <div className="product-highlight-box">
            <img src={process.env.PUBLIC_URL + "/images/ai02.png"} alt="3D 캐릭터 에이전트" />
            <h3>3D 캐릭터 에이전트</h3>
          </div>
        </div>

        <div className="product-full-width-section">
          <h3>AI 분석툴 + 성장형 캐릭터</h3>
          <div className="product-section">
            <div className="product-image">
              <img src={process.env.PUBLIC_URL + "/images/ai03.png"} alt="AI 분석툴 + 성장형 캐릭터" />
            </div>
            <div className="product-content">
              <p>
                {formatText("AI 분석 툴을 통해 학습자의 진행 상황을 정확히 파악하고, 이에 따라 성장하는 3D 캐릭터로 학습 동기를 부여합니다. 개인화된 학습 경험과 시각적 피드백으로 효과적인 언어 습득을 지원합니다.")}
              </p>
              <Link to="/avatar-details" className="product-link-wrapper">
                <button className="product-link active">자세히 보기</button>
              </Link>
            </div>
          </div>
        </div>

        <div className="product-section">
          <div className="product-image">
            <img src={process.env.PUBLIC_URL + "/images/icon05.png"} alt="AI주식거래서비스 - 제우스" />
          </div>
          <div className="product-content">
            <h3>AI주식거래서비스 - 제우스</h3>
            <p>
              {formatText("인공지능이 분석하고 추천하는 스마트한 주식 거래 플랫폼입니다. Google AI (Gemini)와 로컬 AI (Ollama)를 활용해 주식을 심층 분석합니다. 주식투자사 API와 연동해 실시간 시세와 거래를 제공합니다. AI 추천에 따른 자동 매수/매도와 예약 주문을 지원합니다. 모의투자로 전략을 검증하고 안전하게 학습할 수 있습니다.")}
            </p>
            <Link to="/trading" className="product-link-wrapper">
              <button className="product-link active">체험하기</button>
            </Link>
          </div>
        </div>



        <div className="product-section">
          <div className="product-image">
            <img src={process.env.PUBLIC_URL + "/images/icon07.png"} alt="스토리보드AI - STORYBOARD AI" />
          </div>
          <div className="product-content">
            <h3>스토리보드AI - STORYBOARD AI</h3>
            <p>
              {formatText("영상 제작을 위한 AI 스토리보드 제작 플랫폼입니다. 프로젝트, 에피소드, 컷을 구조화해 관리하고 프리뷰와 출력까지 단일 대시보드에서 처리합니다. AI 설정에 키를 연동하면 스토리, 이미지, 영상 생성과 스크립트 지원을 즉시 활성화할 수 있습니다.")}
            </p>
            <a
              href="https://storyboard.ai.kr/intro"
              className="product-link-wrapper"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="product-link active">인트로 보기</button>
            </a>
          </div>
        </div>

        <div className="product-section">
          <div className="product-image">
            <img src={process.env.PUBLIC_URL + "/images/icon01.png"} alt="Icon 1" />
          </div>
          <div className="product-content">
            <h3>AI 영어친구[POC ver.]</h3>
            <p>
              {formatText("학교/기업 등에 AI 튜터가 학습자의 수준과 목표에 맞춘 맞춤형 학습을 제공합니다. 실시간 피드백과 교정 및 그룹 리포터 제공으로 효과적인 언어 습득을 돕습니다.")}
            </p>
            <Link to="/legacy/product" className="product-link-wrapper">
              <button className="product-link active">바로가기</button>
            </Link>
          </div>
        </div>

        <div className="product-section">
          <div className="product-image">
            <img src={process.env.PUBLIC_URL + "/images/icon04.png"} alt="Icon 4" />
          </div>
          <div className="product-content">
            <h3>성장하는 3D AI 캐릭터[MVP 제작중]</h3>
            <p>
              {formatText("사용자의 학습 진도와 연동되어 성장하는 3D 캐릭터를 통해 학습의 몰입감과 친밀도를 높입니다. 캐릭터의 성장이 곧 사용자의 언어 능력 향상을 시각적으로 보여주어 지속적인 학습 동기를 부여합니다.")}
            </p>
            <Link to="/avatar-details" className="product-link-wrapper">
              <button className="product-link active">바로가기</button>
            </Link>
          </div>
        </div>

        <div className="product-section">
          <div className="product-image">
            <img src={process.env.PUBLIC_URL + "/images/icon02.png"} alt="Icon 2" />
          </div>
          <div className="product-content">
            <h3>언어학습 주행로봇</h3>
            <p>
              {formatText("외국인 친구처럼 먼저 말걸어주는 로봇을 통해 다양한 상황별 언어 학습을 경험하세요. 실제 생활과 자연스럽게 언어를 습득할 수 있습니다.")}
            </p>
            <button 
              className="product-link reserve" 
              onClick={() => openReservation('언어학습 주행로봇')}
            >
              예약 신청
            </button>
          </div>
        </div>

        <div className="product-section">
          <div className="product-image">
            <img src={process.env.PUBLIC_URL + "/images/icon03.png"} alt="Icon 3" />
          </div>
          <div className="product-content">
            <h3>학습/여행 AI에이전트</h3>
            <p>
              {formatText("다른 나라를 방문하는 한국인, 방문 여행객 실시간 학습 및 통역을 제공하는 AI 동반자입니다. 언어학습의 두려움 극복 후 세계 곳곳을 탐험하세요.")}
            </p>
            <button 
              className="product-link reserve" 
              onClick={() => openReservation('학습/여행 AI에이전트')}
            >
              예약 신청
            </button>
          </div>
        </div>

        {showReservationModal && (
          <div className="reservation-overlay" onClick={closeReservation}>
            <div className="reservation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="reservation-header">
                <h3>{reservationProduct} 예약 신청</h3>
                <button className="reservation-close" onClick={closeReservation}>×</button>
              </div>
              <p className="reservation-description">
                사전 예약을 통해 출시 일정과 맞춤 제안, 베타 체험 기회를 가장 먼저 받아보세요.
              </p>
              <form className="reservation-form" onSubmit={handleReservationSubmit}>
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="reservation-website">Website</label>
                  <input
                    type="text"
                    id="reservation-website"
                    name="website"
                    value={reservationForm.website}
                    onChange={handleReservationChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label>이름</label>
                  <input
                    type="text"
                    name="name"
                    value={reservationForm.name}
                    onChange={handleReservationChange}
                    placeholder="홍길동"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>이메일</label>
                  <input
                    type="email"
                    name="email"
                    value={reservationForm.email}
                    onChange={handleReservationChange}
                    placeholder="example@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>연락처 (선택)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={reservationForm.phone}
                    onChange={handleReservationChange}
                    placeholder="010-1234-5678"
                  />
                </div>
                <div className="form-group">
                  <label>요청 사항</label>
                  <textarea
                    name="message"
                    value={reservationForm.message}
                    onChange={handleReservationChange}
                    placeholder="프로덕트 도입 목적, 희망 기능 등을 알려주세요."
                    rows="4"
                  />
                </div>

                {reservationStatus.message && (
                  <div className={`reservation-status ${reservationStatus.type}`}>
                    {reservationStatus.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="product-link active submit"
                  disabled={reservationLoading}
                >
                  {reservationLoading ? '전송 중...' : '예약 신청 보내기'}
                </button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Product;